import {QueueItemType} from "../../types";
import QueueItemVideo from "./QueueItemVideo";
import QueueItemLink from "./QueueItemLink";
import QueueItemImage from "./QueueItemImage";
import QueueItemYoutube from "./QueueItemYoutube";
import QueueItemText from "./QueueItemText";
import image from "../../assets/imgs/profilePic.png";
import React, {useEffect, useRef, useState} from "react";
import AuthorBadgeByUuid from "../../components/AuthorBadgeByUuid/AuthorBadgeByUuid";
import {apiDelete, apiPut, extractPayloadFromResponse} from "../../services/apiService";
import {useAuth} from "../../providers/AuthProvider";
import {useSiteMessage} from "../../providers/SiteMessageProvider";
import {AuthorBadgeVariant, Button, ButtonVariantProps, Card} from "@varymadellc/my-weed-club-component-library";
import cx from "classnames";
import Loading from "../../components/Loading/Loading";
import {HashTagsCollection} from "../../components/HashTagsCollection";
import {FiFacebook, FiLink, FiMail} from "react-icons/fi";
import QueueItemPintrest from "@/components/Queue/QueueItemPintrest";
import copyText from "@/utils/copyText";
import QueueItemGiphy from "@/components/Queue/QueueItemGiphy";
import facebookShare from "@/utils/facebookShare";
import {IoFlame} from "react-icons/io5";
import tagAddedCallbackHandler from "@/services/tagAddedCallbackHandler";
import tagAddedToQueueItemCallbackHandler from "@/services/tagAddedToQueueItemCallbackHandler";
import QueueItemProfile from "@/components/Queue/QueueItemProfile";

export interface QueueItemProps {
    data?: QueueItemType;
    showAuthorBadge?: boolean;
    showInstantly?: boolean;
    index?: number;
    preview?: boolean;
    limitedAccess?: boolean;

    [key: string]: any;
}

const deleteQueueItem = async (uuid: string, authToken: string, setMessage: (message: string) => void) => {
    try {
        const confirmed = window.confirm('Are you sure you want to delete this item?');

        if (!confirmed) return;

        const response = await apiDelete(`/q/${uuid}`, authToken);

        const {error} = await extractPayloadFromResponse(response);

        if (error) {
            setMessage(error);

            return;
        }

        setMessage('Deleted, Queue will refresh shortly');
    } catch (error: any) {
        setMessage(error.message);
    }
}

export const buildQueueItem = (queueItem: QueueItemType, index = 0) => {
    switch (queueItem.type) {
        case 'profile':
            return <QueueItemProfile data={queueItem}/>;
        case 'text':
            return <QueueItemText data={queueItem}/>;
        case 'youtube':
            return <QueueItemYoutube data={queueItem}/>;
        case 'giphy':
            return <QueueItemGiphy data={queueItem}/>;
        case 'video':
            return <QueueItemVideo index={index} data={queueItem}/>;
        case 'link':
            return <QueueItemLink data={queueItem}/>;
        case 'pintrest':
            return <QueueItemPintrest data={queueItem}/>;
        case 'image':
        case 'photo':
            return <QueueItemImage data={queueItem}/>;
        // case 'audio': return <QueueItemAudio data={buildQueueItem}/>
        default:
            return 'who know?';
    }
}

const renewPost = async (post: any, user: any, authToken: string, setMessage: any) => {
    try {
        if (!post) {
            return;
        }

        const response = await apiPut(`/q/renew/${post.uuid}`, undefined, authToken);

        const {error, data} = await extractPayloadFromResponse(response);

        if (error) {
            throw new Error(error);
        }

        setMessage('Reposted');
    } catch (error: any) {
        setMessage(error.message);
    }
}

export default function QueueItem({
                                      data,
                                      showAuthorBadge = true,
                                      index = 0,
                                      showInstantly = false,
                                      preview = false,
                                      limitedAccess,
                                      ...rest
                                  }: QueueItemProps) {
    const {userCan, user, authToken} = useAuth();

    const {setMessage} = useSiteMessage();

    const [readyToLoad, setReadyToLoad] = useState<boolean>(showInstantly);

    const ref = useRef<HTMLLIElement>(null);

    useEffect(() => {
        let timer;

        if (ref.current && !showInstantly) {
            const observer = new IntersectionObserver((entries) => {
                entries.forEach((entry) => {
                    if (entry.isIntersecting) {
                        setReadyToLoad(true);
                        observer && observer.unobserve(entry.target);
                    }
                });
            });

            observer.observe(ref.current);
        }
    }, [ref.current]);

    if (!data) {
        return null;
    }

    const copyTextHandler = (wasCopied) => {
        setMessage(wasCopied ? 'Copied' : 'Failed to copy')
    };

    const postLink = `${location.protocol + '//' + location.host}/q/${data?.uuid}`;

    if (preview) {
        return (
            <li key={data.uuid} className={cx({"border-4 border-yellow-400": data?.metadata?.originalAccountUuid})} {...rest}>
                <a id={data.uuid}/>
                <h3 className="bg-secondary text-xs p-1 text-center">Type: {data.type.replace('text', 'message')}</h3>
                {buildQueueItem(data)}
            </li>
        );
    }

    return (
        <li key={data.uuid} ref={ref} className="queue-item-marker relative z-1 mb-4 bg-dark-black border border-primary/50" {...rest}>
            <a id={data.uuid}/>
            <div className="relative">
                {!limitedAccess && (
                    <div className="flex relative gap-2 z-[1000] justify-end">
                        <Button onClick={() => {
                            facebookShare(postLink);
                        }}>
                            <FiFacebook/>
                        </Button>
                        <Button onClick={() => {
                            const toEmail = '';
                            const subject = 'Content from MyWeed.Club';
                            const body = `Thank you for sharing. Please save ${postLink} in your bookmarks to share more content in the future.`;

                            const mailtoUrl = `mailto:${toEmail}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;

                            window.open(mailtoUrl, '_blank');
                        }}>
                            <FiMail/>
                        </Button>
                        <Button onClick={() => {
                            copyText(postLink, copyTextHandler);
                        }}>
                            <FiLink/>
                        </Button>
                    </div>
                )}
                <div className={cx({"border-4 border-yellow-400 relative": data?.metadata?.originalAccountUuid})}>
                    {
                        (data?.metadata?.originalAccountUuid) && (
                            <div
                                className="text-xs bg-yellow-400 text-black w-[10%] text-center absolute left-0 top-0 h-6 capitalize z-[1000] rounded-br-lg">RELIT</div>
                        )
                    }
                    {buildQueueItem(data, index)}
                </div>
            </div>
            {
                (data && readyToLoad) ? (
                        <>
                            {showAuthorBadge && !limitedAccess &&  (
                                <div className="flex flex-col p-2">
                                    <div className="flex-1 flex flex-col sm:flex-row items-start w-full flex-wrap">
                                        {
                                            data?.metadata?.originalAccountUuid && (
                                                <AuthorBadgeByUuid variant={AuthorBadgeVariant.default}
                                                                   className="flex-1 bg-secondary !text-black !border-black"
                                                                   uuid={data?.metadata?.originalAccountUuid}/>
                                            )
                                        }
                                        <div className="flex flex-1 flex-row">
                                            {
                                                data?.metadata?.originalAccountUuid && (
                                                    <p className="text-white m-2 ml-8">Relighted by:</p>
                                                )
                                            }
                                            <AuthorBadgeByUuid
                                                className="flex-1 border-none bg-secondary"
                                                variant={data?.metadata?.originalAccountUuid ? AuthorBadgeVariant.small : AuthorBadgeVariant.default}
                                                uuid={data?.metadata.addedByAccountUuid || data.AccountUuid}/>
                                        </div>
                                    </div>
                                    <div className="flex-1 flex flex-row w-full flex-wrap gap-2 p-2">
                                        <div className="text-xs text-white">Posted
                                            At: {(new Date(data.updatedAt)).toLocaleDateString('US-en', {
                                                year: 'numeric',
                                                month: 'numeric',
                                                day: 'numeric',
                                                hour: 'numeric',
                                                minute: 'numeric',
                                                second: 'numeric'
                                            })}</div>
                                    </div>
                                    <div className="flex flex-row sm:flex-row">
                                        <div className="flex-1 flex justify-end text-black flex-col">
                                            {/*<div className="text-lg">Type: {data.type.toUpperCase()}</div>*/}
                                        </div>
                                    </div>
                                </div>
                            )}
                            <hr className="border-primary m-2"/>
                            <div className="flex flex-row p-2">
                                {
                                    !limitedAccess && (
                                        <div className="flex-1 flex flex-col">
                                            {
                                                index !== 0 && (
                                                    <>
                                                        <div className="flex-1 flex gap-2">
                                                            <Button variant={'secondary' as ButtonVariantProps.secondary}
                                                                    type="button"
                                                                    onClick={() => renewPost(data, user, authToken, setMessage)}>
                                                            <span>
                                                                <IoFlame
                                                                    className="inline-block w-[30px] h-[30px]"/> Relight
                                                            </span>
                                                            </Button>
                                                        </div>
                                                    </>
                                                )
                                            }
                                        </div>
                                    )
                                }
                                <div className="flex-1 flex flex-col">
                                    {
                                        (
                                            (userCan('ROOT') || data && data?.AccountUuid === user?.uuid)
                                            && !limitedAccess
                                        ) && (
                                            <div className="flex flex-row justify-end">
                                                <Button onClick={async (event: any) => {
                                                    event.preventDefault();

                                                    await deleteQueueItem(data.uuid, authToken, setMessage);
                                                }} variant={'link' as ButtonVariantProps.link} type="button"
                                                        className="text-white">Delete</Button>
                                            </div>
                                        )
                                    }
                                </div>
                            </div>
                            {!limitedAccess && <HashTagsCollection tagAddedCallback={async (collectionUuid, tagUuid) => {
                                await tagAddedToQueueItemCallbackHandler(authToken, collectionUuid, tagUuid, data?.metadata?.originalAccountUuid || data?.AccountUuid);
                            }} uuid={data.uuid}/>}
                        </>
                    ) :
                    <Card className="text-center"><Loading hasOverlay={true}/></Card>
            }
        </li>
    );
}